/**
 * Zod schema for the order's card-hold projection (`order.cardHold`) and the
 * server close stamp. Mirrors `packages/models/src/orders/cardHold.ts` —
 * docs/CARD_HOLDS.md.
 *
 * Declared on `OrderSchema` / `OrderDataSchema` because a plain `z.object()`
 * STRIPS undeclared keys: an order parsed and written back by the API would
 * otherwise lose the "card on file" chip. The stored map is normalized by the
 * model's defensive parser first, so a malformed projection reads as ABSENT
 * (never a failed order parse), and any key the projection does not declare —
 * a token above all — is dropped.
 */

import { z } from 'zod';
import type { IOrderCardHold } from '@order-up/models';

/**
 * The stored projection — KEPT, not re-validated. These schemas exist so an
 * order the API parses and writes back does not silently drop the field (a
 * plain `z.object()` strips undeclared keys). The projection is written ONLY by
 * the server (`CardHoldService`, built from an allow-list — `orderProjectionOf`)
 * and refused to every client by firestore.rules; its reader-side allow-list is
 * `parseOrderCardHold` (@order-up/models). Re-running that parser here would put
 * it in every web bundle that parses an order — checkout's first download among
 * them — for a field no web surface reads. A non-object value is dropped.
 */
export const OrderCardHoldSchema = z.custom<IOrderCardHold>().optional();

/**
 * `closedBy` / `closeReason` — server-written stamps, kept as stored. Declared
 * the same cheap way: the order schemas ship in checkout's first download.
 */
export const OrderServerCloseStampSchema = z.custom<string>().optional();
